import { FaBars, FaTimes, FaUserCircle } from 'react-icons/fa';
import { FiChevronDown } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/store';
import { useMemo, useState } from 'react';
import AuthAction from '../store/auth/action';
import app_logo from '../assets/app_logo_no_text.png';

interface NavBarProps {
    menuOpen?: boolean;
    setMenuOpen?(value: boolean): void;
}

type NavItem = {
    to: string;
    path: string;
    label: string;
    authOnly?: boolean;
    adminOnly?: boolean;
};

const MAIN_NAV_ITEMS: NavItem[] = [
    { to: '/', path: '/home', label: 'Ballina' },
    { to: '/favorites', path: '/favorites', label: 'Të preferuarat' },
    { to: '/my-posts', path: '/my-posts', label: 'Shpalljet e mia', authOnly: true },
    { to: '/articles', path: '/articles', label: 'Artikuj' },
    { to: '/contact', path: '/contact', label: 'Kontakto' },
    { to: '/admin', path: '/admin', label: 'Admin Panel', adminOnly: true },
];

const Navbar = ({ menuOpen, setMenuOpen }: NavBarProps) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { user } = useAppSelector((state) => state.auth);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const isAdmin = user?.role === 'admin';
    const hasExternalSidebar = menuOpen !== undefined && !!setMenuOpen;

    const closeDropdown = () => setDropdownOpen(false);

    const navItems = useMemo(
        () =>
            MAIN_NAV_ITEMS.filter((item) => {
                if (item.authOnly && !user) return false;
                if (item.adminOnly && !isAdmin) return false;
                return true;
            }),
        [user, isAdmin],
    );

    const navLinkClass = (path: string) => {
        const isActive =
            location.pathname === path ||
            (path === '/home' && (location.pathname === '/' || location.pathname === '/home'));
        return `px-3 py-2 rounded-lg font-medium text-sm transition whitespace-nowrap shrink-0 ${isActive ? 'text-[#704e37] bg-[#704e37]/10' : 'text-gray-600 hover:text-[#704e37] hover:bg-[#704e37]/5'}`;
    };

    return (
        <nav className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
                <div className="flex items-center gap-4 lg:gap-8 min-w-0">
                    {hasExternalSidebar && (
                        <button
                            type="button"
                            onClick={() => setMenuOpen!(!menuOpen)}
                            className="p-2 -ml-2 text-[#704e37] hover:bg-[#704e37]/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#704e37]/30 shrink-0"
                            aria-label={menuOpen ? 'Mbyll menunë' : 'Hap menunë'}
                        >
                            {menuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
                        </button>
                    )}
                    <Link to="/" className="flex items-center gap-2 group shrink-0 min-w-0">
                        <img src={app_logo} alt="Logo" className="h-8 w-8 shrink-0" />
                        <span className="text-lg sm:text-xl font-bold text-[#704e37] group-hover:opacity-90 truncate">
                            Veshje për ty
                        </span>
                    </Link>
                    <div className="hidden md:flex items-center gap-1">
                        {navItems.map((item) => (
                            <Link key={item.path} to={item.to} className={navLinkClass(item.path)}>
                                {item.label}
                            </Link>
                        ))}
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    {user ? (
                        <div className="flex items-center gap-2">
                            <span className="hidden sm:block text-sm text-gray-600 max-w-[120px] truncate">
                                {user.first_name} {user.last_name}
                            </span>
                            <button
                                type="button"
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="flex items-center gap-1.5 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#704e37]/30"
                                aria-label="Menuja e llogarisë"
                                aria-expanded={dropdownOpen}
                            >
                                <FaUserCircle className="text-2xl text-[#704e37]" />
                                <FiChevronDown
                                    className={`text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                                />
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Link
                                to="/login"
                                className="px-3 sm:px-4 py-2 rounded-lg text-[#704e37] font-medium hover:bg-[#704e37]/5 transition text-sm"
                            >
                                Kyçuni
                            </Link>
                            <Link
                                to="/signup"
                                className="px-3 sm:px-4 py-2 rounded-lg bg-[#704e37] text-white font-medium hover:bg-[#704e37]/90 transition text-sm"
                            >
                                Regjistrohuni
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile / small tablet: horizontal scroll strip under the bar */}
            <div className="md:hidden border-t border-gray-100 bg-white/95">
                <div
                    className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto py-2 scroll-smooth [-webkit-overflow-scrolling:touch]"
                    role="navigation"
                    aria-label="Navigimi kryesor"
                >
                    {navItems.map((item) => (
                        <Link key={item.path} to={item.to} className={navLinkClass(item.path)}>
                            {item.label}
                        </Link>
                    ))}
                </div>
            </div>

            {user && dropdownOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={closeDropdown} aria-hidden="true" />
                    <div className="absolute right-4 top-full mt-2 w-52 bg-white rounded-xl border border-gray-100 shadow-lg py-2 z-50">
                        <div className="px-4 py-2 border-b border-gray-100 sm:hidden">
                            <p className="text-sm font-medium text-gray-800 truncate">
                                {user.first_name} {user.last_name}
                            </p>
                        </div>
                        <Link
                            to="/profile"
                            className="block px-4 py-2.5 text-gray-700 hover:bg-[#704e37]/5 hover:text-[#704e37]"
                            onClick={closeDropdown}
                        >
                            Profili
                        </Link>
                        {/* <Link to="/orders" className="block px-4 py-2.5 text-gray-700 hover:bg-[#704e37]/5 hover:text-[#704e37]" onClick={closeDropdown}>
                            Porositë
                        </Link> */}
                        <Link
                            to="/contact"
                            className="block px-4 py-2.5 text-gray-700 hover:bg-[#704e37]/5 hover:text-[#704e37]"
                            onClick={closeDropdown}
                        >
                            Kontakto
                        </Link>
                        {isAdmin && (
                            <Link
                                to="/admin"
                                className="block px-4 py-2.5 text-[#704e37] hover:bg-[#704e37]/5"
                                onClick={closeDropdown}
                            >
                                Admin Panel
                            </Link>
                        )}
                        <button
                            type="button"
                            className="w-full text-left px-4 py-2.5 text-gray-700 hover:bg-red-50 hover:text-red-700"
                            onClick={() => {
                                closeDropdown();
                                AuthAction.signOut(dispatch);
                            }}
                        >
                            Dilni
                        </button>
                    </div>
                </>
            )}
        </nav>
    );
};

export default Navbar;
